import React, {useCallback, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {PrescriptionTooltip, Tooltip} from '@optimaxdev/design-system';
import {OdAndOsType} from '@optimaxdev/design-system/dist/types/components/prescriptionTable/types';
import classNames from 'classnames';

import {CART_UPGRADE_PRESCRIPTION} from 'constants/analytics/events/cartUpgrade';
import {PRESCRIPTION_METHOD} from 'constants/product';
import {OculusPrescriptionType, PrescriptionType} from 'desktop/types/checkoutItemsType';
import {apiConfig} from 'libs/api/api';
import {logWithSentry} from 'libs/sentry/sentry';
import {spawnPriorityTask} from 'libs/timers/spawnPriorityTask/spawnPriorityTask';

import s from './prescriptionVariations.scss';

const defaultAnalytic = () => {
    sendDesktopAnalyticsEvent(CART_UPGRADE_PRESCRIPTION, 'Send later');
};

/**
 * Render send-by-email prescription
 */
export const EmailPrescriptionTooltip = ({analytics = defaultAnalytic}) => {
    const [isAnalyticsAlreadySent, setAnalyticsAlreadySent] = useState<boolean>();
    const onSendAnalytics = useCallback(
        (visible: boolean) => {
            if (visible && !isAnalyticsAlreadySent) {
                spawnPriorityTask(analytics);
                setAnalyticsAlreadySent(true);
            }
        },
        [isAnalyticsAlreadySent, analytics],
    );

    return (
        <Tooltip
            content={
                <div className={s.tooltipContent}>
                    <p>
                        Whenever suits you, don&apos;t forget to send us your
                        <br />
                        prescription, both email (prescription@glassesusa.com)
                        <br />
                        or fax (1-888-250-6925) are available for you.
                    </p>
                </div>
            }
            closeTrigger="hover"
            onVisibilityChanged={onSendAnalytics}
            openTrigger="hover"
            position="bottom-end"
        >
            <span>Send Prescription Later</span>
        </Tooltip>
    );
};

/**
 * Render uploaded prescription
 * @param {{link: string}} props - component props
 * @return {Node} - rendered prescription
 */
export const UploadPrescription = ({link}: {link: string}) => {
    const openImageOnNewTab = async () => {
        try {
            const response = await apiConfig.instanceGusaBackend(link, {
                baseURL: '',
                responseType: 'blob',
            });
            const imgUrl = URL.createObjectURL(response.data);
            const tab = window.open(imgUrl, '_blank');
            if (tab !== null) {
                tab.focus();
            }
        } catch (e) {
            logWithSentry(e);
        }
    };

    return (
        <span className={s.uploadText}>
            <button
                className={s.link}
                data-test-name="viewPrescription"
                onClick={openImageOnNewTab}
                type="button"
            >
                View Prescription
            </button>
        </span>
    );
};

/**
 * Render filled online prescription
 * @param {{prescription: CartItemEyeglassesPrescriptionType}} props - component props
 * @return {Node} - rendered prescription
 */
export const OnlinePrescription = ({
    analytics = () => sendDesktopAnalyticsEvent(CART_UPGRADE_PRESCRIPTION, 'View'),
    isSingleVision,
    prescription,
}: {
    prescription: PrescriptionType;
    analytics?: () => void;
    isSingleVision: boolean;
}) => {
    const [isAnalyticsAlreadySent, setAnalyticsAlreadySent] = useState<boolean>();

    const onSendAnalytics = useCallback(
        (visible: boolean) => {
            if (visible && !isAnalyticsAlreadySent) {
                analytics();
                setAnalyticsAlreadySent(true);
            }
        },
        [isAnalyticsAlreadySent, analytics],
    );

    const isForReaders = prescription.method === PRESCRIPTION_METHOD.MAGNIFICATION_METHOD;
    const isMultiPd = prescription.pd?.left !== 'None' && prescription.pd?.right !== 'None';

    const osOdReaders = {
        add: '',
        axi: '',
        bc: '',
        boxes: '',
        color: '',
        color_code: '',
        cyl: '',
        dia: '',
        sph: prescription.strength,
    };

    const getOsOd = (values: OculusPrescriptionType) => {
        return isSingleVision ? {...values, add: ''} : values;
    };

    const multiPd = {
        left: prescription.pd.left,
        near: prescription.pd.near,
        right: prescription.pd.right,
        singular: 'None',
    };

    const singlePd = isSingleVision ? {...prescription.pd, near: 'None'} : prescription.pd;

    return (
        <PrescriptionTooltip
            tooltipSettings={{
                children: '',
                closeTrigger: 'hover',
                containerClassName: classNames('fs-exclude', s.container),
                content: '',
                onVisibilityChanged: onSendAnalytics,
                openTrigger: 'hover',
                position: 'bottom',
            }}
            comment={prescription.comment}
            name={prescription.name}
            od={isForReaders ? osOdReaders : (getOsOd(prescription.od) as OdAndOsType)}
            os={isForReaders ? osOdReaders : (getOsOd(prescription.os) as OdAndOsType)}
            pd={isMultiPd ? multiPd : singlePd}
        >
            <span data-test-name="viewPrescription">View Prescription</span>
        </PrescriptionTooltip>
    );
};
